import type { ComponentPropsWithRef } from 'react';

import { use } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IconName } from '../Icon/Icon';

import { Button } from '../Button/Button';
import { ButtonGroup } from '../Button/ButtonGroup';
import { Menu } from '../Menu/Menu';
import { FloatingMenuTrigger } from './FloatingAction';
import { FloatingMenuContext, FloatingRootPortalContext } from './HeaderActions.context';
import { useIsFloatingViewport } from './HeaderActions.utils';

// Header Action Group

type HeaderActionGroupProps = ComponentPropsWithRef<typeof ButtonGroup> & {
  /** Since a group becomes a floating menu, it needs an icon that represents its actions. */
  floatingIcon: IconName;
  /** Shown on the floating menu trigger, because its actions are hidden until the menu opens. */
  loading?: boolean;
};

export const HeaderActionGroup = ({ children, floatingIcon, loading, ...delegated }: HeaderActionGroupProps) => {
  const { t } = useTranslation(['common']);

  const floatingRootPortal = use(FloatingRootPortalContext);
  const isFloatingViewport = useIsFloatingViewport();

  if (!isFloatingViewport) return <ButtonGroup {...delegated}>{children}</ButtonGroup>;

  if (!floatingRootPortal) return null;

  return createPortal(
    <Menu.Root>
      <Menu.Trigger render={<FloatingMenuTrigger aria-label={t('common:actions.openMenu')} icon={floatingIcon} loading={loading} />} />

      <Menu.List side="top">
        <FloatingMenuContext value={true}>{children}</FloatingMenuContext>
      </Menu.List>
    </Menu.Root>,
    floatingRootPortal,
  );
};

// Header Action Group Menu

type HeaderActionGroupMenuProps = Omit<ComponentPropsWithRef<typeof Button> & { children?: never }, 'children' | 'aria-label' | 'icon'> &
  Pick<ComponentPropsWithRef<typeof Menu.List>, 'children'>;

/** The split part of a header action group. In the group's floating menu, its items are listed with the other actions. */
export const HeaderActionGroupMenu = ({ children, ...delegated }: HeaderActionGroupMenuProps) => {
  const { t } = useTranslation(['common']);

  const isFloatingMenu = use(FloatingMenuContext);

  if (isFloatingMenu) return children;

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button {...delegated} aria-label={t('common:actions.openMenu')} emphasis={delegated.emphasis ?? 'primary'} icon="ChevronDown" />
        }
      />

      <Menu.List>{children}</Menu.List>
    </Menu.Root>
  );
};
